import { Box, Grid, Skeleton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { SplashScreen } from "src/components/loading-screen";
import ChartLine from "src/sections/charts/chart-line";
import { useEffect } from "react";
import { setExtraOptionsLoaded, setFilterCompare, setFilterDate, setFilterDateCompare, setFilterLoadedCountry, setFilterLoadedDevice, setFilterLoadedKeyword, setFilterLoadedPlatform, setFilterMinDatePicker, setFilterTimestep } from "src/context/reducers/filter-settings/filterSlice";
import KpiToggle, { useKPIToggle } from "../../kpi-toggle";
import { RootState } from "src/context/store";
import TabulableTable from "../../tabulable-table";
import { formatDate } from "src/utils/dates-labels";
import { useLocales } from "src/locales";
import { subDays } from "date-fns";
import { fDate, getLast12WeeksDateRange } from "src/utils/format-time";
import { Helmet } from "react-helmet-async";

export default function ActivationsAnalyzeView() {
    const filterStore = useSelector((state: RootState) => state.filters);
    const { currentLang: { value: locale } } = useLocales();

    const dispatch = useDispatch();

    const toggle = useKPIToggle(filterStore.extraOptions?.mode === "activations" ? ["ad_brand_traffic", "cpc"] : ["brand_searches", "total_brand_traffic"]);


    useEffect(() => {
        dispatch(setFilterTimestep("year_week"))
    }, [])

    useEffect(() => {
        if (toggle.statsData) {
            dispatch(setFilterMinDatePicker("2024-01-01"))
        }
    }, [toggle.statsData, dispatch]);

    useEffect(() => {
        if (toggle.statsData?.filters?.keywords) {
            dispatch(setFilterLoadedKeyword(toggle.statsData.filters.keywords.map((keyword: any) => ({ search_term: keyword.keyword, total_organic_searches: keyword.total_organic_searches }))));
        }
        if (toggle.statsData?.filters?.devices) {
            dispatch(setFilterLoadedDevice(toggle.statsData.filters.devices));
        }
        if (toggle.statsData?.filters?.countries) {
            dispatch(setFilterLoadedCountry(toggle.statsData.filters.countries));
        }
        if (toggle.statsData?.filters?.platforms) {
            dispatch(setFilterLoadedPlatform(toggle.statsData.filters.platforms?.map((platform: any) => platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase())));
        }
    }, [toggle.statsData?.filters, dispatch]);

    useEffect(() => {
        dispatch(setExtraOptionsLoaded(toggle.statsData?.filters));
    }, [toggle.statsData?.filters, dispatch]);

    useEffect(() => {
        const { start, end } = getLast12WeeksDateRange();
        dispatch(setFilterDate({ start, end }));
        dispatch(setFilterCompare(false))

        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
    }, [])

    return (
        <>
            <Helmet>
                <title>Explore</title>
            </Helmet>
            <Box px={"24px"}>
                {(toggle.isLoadingStats || toggle.isLoadingCompareStats || !toggle.statsData) ? (
                    <Box display={"flex"} flexDirection={'row'} mb={2}>
                        <Grid container spacing={2}>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <Grid key={index} item sx={{ flex: '0 0 19.8%', minWidth: 210 }}>
                                    <Skeleton
                                        width="100%"
                                        height={100}
                                        variant="rectangular"
                                        animation="wave"
                                        sx={{
                                            borderTopLeftRadius: index === 0 ? "12px" : 0,
                                            borderBottomLeftRadius: index === 0 ? "12px" : 0
                                        }}
                                    />
                                </Grid>
                            ))}

                        </Grid>
                        <Skeleton
                            width="3%"
                            height={100}
                            variant="rectangular"
                            animation="wave"
                            sx={{
                                borderTopRightRadius: "12px",
                                borderBottomRightRadius: "12px"
                            }}
                        />
                    </Box>
                ) : (
                    <div style={{ position: 'relative' }}>
                        <KpiToggle {...toggle} />
                    </div>
                )}


                {(toggle.isLoadingStats || toggle.isLoadingCompareStats || !toggle.statsData) ? (
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width="100%"
                        height={403}
                    />
                )
                    :
                    (
                        <ChartLine
                            enableIncompleteHighlight={true}
                            chart={{
                                series: [
                                    {
                                        categories: toggle.statsData?.time_series_data?.map((item: any) => filterStore?.timestep === "date_day" ? fDate(item.date, "yyyy-MM-dd") : item?.date) ?? [],
                                        data: toggle.kpiToggle.map((kpi) => {
                                            const stat = toggle.stats.find((stat) => stat.key === kpi);
                                            if (!stat) return [];
                                            const data = [({
                                                name: stat?.title,
                                                data: toggle.statsData?.time_series_data?.map((item: any) => item[kpi] || 0) ?? [],
                                                visible: true,
                                                dashStyle: "solid",
                                                color: stat.color,
                                                yAxis: 1,
                                            })]
                                            if (filterStore?.compare) {
                                                data.push({
                                                    name: stat?.title + " Compare",
                                                    data: toggle.compareStatsData?.time_series_data?.map((item: any) => item[kpi] || 0) ?? [],
                                                    visible: true,
                                                    dashStyle: "ShortDash",
                                                    color: stat.color,
                                                    yAxis: 1,
                                                })
                                            }
                                            return data;
                                        }).flat(),
                                    }
                                ]
                            }}
                        />
                    )}
                {(toggle.isLoadingStats || toggle.isLoadingCompareStats || !toggle.statsData) ? (
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width="100%"
                        height={403}
                        sx={{ borderRadius: 2, mt: 2 }}
                    />
                ) : <TabulableTable />}
            </Box>
        </>
    )
}